import React, { memo, useCallback, useMemo } from 'react';

import type { FieldData } from '../../../common/types';
import { queryClient } from 'common/providers/QueryProvider';
import { JOB_STATUS_KEY } from 'create/bulk/common/hooks/useBulkGeneratePlantingPrescription/const';
import { useAppSelector } from 'app/hooks';
import { convertForLocaleScalar } from 'util/unitConversion';
import { getAxisBins, getAxisBounds } from 'util/formatters/data-viz/axis';
import { ADVANCED_SEED_BIN_ROUNDING_VALUE, NUM_SEED_RATE_BINS } from 'constants/data-viz';
import { getProductValidationValues } from 'user/selectors';
import { getActiveCropDfpUuid } from 'current/selectors';
import { getCropById } from 'crops/selectors';

import ZonesTable from './ZonesTable';
import ReviewScriptMap from 'mapping/ReviewScriptMap';
import ProductRateLegend from 'mapping/legends/ProductRateLegend';
import { applicationTypes } from 'constants/scripts';
import type {
  PrescriptionGenerationJobQueryResponse
} from 'create/bulk/common/hooks/useBulkGeneratePlantingPrescription/types';
interface ScriptDetailsProps {
  parentRowData: FieldData;
}

const baseString = 'bulk-review-script-details';

export const ScriptDetails = memo(function ScriptDetails({ parentRowData }: ScriptDetailsProps) {
  const data = queryClient.getQueryData<PrescriptionGenerationJobQueryResponse>([JOB_STATUS_KEY, parentRowData.id]);
  const { zones } = data.prescriptionGenerationJob.result;
  const productValidationValues = useAppSelector(getProductValidationValues);
  const activeCropUuid = useAppSelector(getActiveCropDfpUuid);
  const cropCode = useAppSelector((state) => getCropById(state, activeCropUuid));
  const [selectedZoneId, setSelectedZoneId] = React.useState<string | null>(null);

  const bins = useMemo(() => {
    const convertedSeedRates = zones.map(({ seedingRate }) => {
      return convertForLocaleScalar({
        quantity: Math.ceil(seedingRate.v),
        unit: seedingRate.u,
        unitType: 'seed-density'
      });
    });

    const bounds = getAxisBounds({
      range: convertedSeedRates,
      defaultRange: productValidationValues?.[`PLANTING.${cropCode?.toUpperCase()}.RATE`]
    });

    return getAxisBins({
      axisBounds: bounds,
      binRoundingValue: ADVANCED_SEED_BIN_ROUNDING_VALUE,
      numBins: NUM_SEED_RATE_BINS
    }) as [number, number][];
  }, [productValidationValues, cropCode, zones]);

  const handleClickZone = useCallback((zoneId: string | null = null) => {
    setSelectedZoneId((prev) => (prev === zoneId ? null : zoneId));
  }, []);

  return (
    <section className={baseString}>
      <ZonesTable zones={zones} seedingRateBins={bins} selectedZoneId={selectedZoneId} onClickZone={handleClickZone} />
      <ReviewScriptMap
        fieldUuid={parentRowData.id}
        onClickZone={handleClickZone}
        selectedZoneId={selectedZoneId}
        seedingRateBins={bins}
        zones={zones}
      />
      <ProductRateLegend
        productType={applicationTypes.SEED}
        bins={bins}
        className={`${baseString}__legend`}
      />
    </section>
  );
},
);
